import { useState, useMemo } from 'react';
import type { Photo } from '../types';

interface PolaroidPhotoProps {
  photo: Photo;
  onClick: () => void;
  style?: React.CSSProperties;
  className?: string;
  showDetails?: boolean;
}

export default function PolaroidPhoto({ 
  photo, 
  onClick, 
  style, 
  className = '',
  showDetails = false 
}: PolaroidPhotoProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Random rotation between -5 and 5 degrees
  const rotation = useMemo(() => Math.random() * 10 - 5, []);

  const isVideo = photo.fileType === 'video' || photo.mimeType?.startsWith('video/');

  return (
    <div
      onClick={onClick}
      className={`polaroid cursor-pointer transform transition-all duration-300 hover:z-10 ${className}`}
      style={{
        ...style,
        transform: `rotate(${rotation}deg)`,
      }}
    >
      <div className="relative aspect-[3/4] bg-autumn-cream/50 overflow-hidden">
        {!hasError ? (
          <>
            {isVideo ? (
              <div className="w-full h-full relative">
                <video
                  src={photo.thumbnailUrl || photo.fileUrl}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${
                    isLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoadedData={() => setIsLoaded(true)}
                  onError={() => setHasError(true)}
                  muted
                  playsInline
                />
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-autumn-brown ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={photo.thumbnailUrl || photo.fileUrl}
                alt={`Photo by ${photo.uploaderName}`}
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  isLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setIsLoaded(true)}
                onError={() => setHasError(true)}
                loading="lazy"
              />
            )}
            
            {/* Loading skeleton */}
            {!isLoaded && (
              <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-autumn-cream to-autumn-gold/20" />
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-autumn-cream">
            <svg className="w-12 h-12 text-autumn-light-brown/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      
      {/* Bottom section with name */}
      {showDetails && (
        <div className="pt-2 text-center">
          <p className="font-body text-sm text-autumn-brown truncate">
            {photo.uploaderName}
          </p>
          {photo.message && (
            <p className="font-body text-xs text-autumn-light-brown/70 truncate mt-1 italic">
              {photo.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

